import { type CSSProperties, type FC, type MouseEvent, useCallback, useMemo } from "react";

import { cx } from "@emotion/css";
import { Button, Colors } from "@snapchat/snap-design-system";
import { CheckIcon, EllipsisIcon } from "@snapchat/snap-design-system-icons";

import { type MyLensesLens, TosKey } from "../../../../gql-generated/types";
import {
    AdsAvailabilitySection,
    IconSection,
    LensContainer,
    LensDataContainer,
    LensDestinationsTooltip,
    LensIcon,
    LensStatusWithWarning,
    LensUpdateAtDate,
    StatusSection,
    tableTextStyle,
} from "../../../../lens/components/shared";
import { MeatBallsMenu } from "../../../../lens/components/shared/meatballs-menu";
import { useLensActionsState, useLensDestinationTypesFromLens, useSubmissionErrors } from "../../../../lens/hooks";
import { LOCKED_BASE_STATUSES } from "../../../../lens/state";
import { getLensScanCode } from "../../../../lens/utils/getLensScanCode";
import {
    VISIBILITY_OPTION_ICON_MAP,
    VISIBILITY_OPTION_LABEL_MAP,
    VisibilityLensRow,
} from "../../../../lens-visibility/components";
import { Flexbox, NotAvailableBar, TruncateComponent } from "../../../../shared/components";
import { ROLE_ACTIONS } from "../../../../shared/constants";
import { useHoveredState, useShouldShowAdsAvailability, useTos } from "../../../../shared/hooks";
import { usePermittedBasedOnLensFolderRoleAction } from "../../../../shared/hooks/roles";
import { isAvailableForAds } from "../../../../shared/models";
import { copyToClipboard } from "../../../../shared/utils/browser";
import { type LensModalProps } from "../../../models";
import { actionsWidth, adsAvailabilityWidth, iconWidth, nameWidth, statusWidth, visibilityWidth } from "../header";

import { CopyDeeplink } from "./CopyDeeplink";
import { GameLensBadge } from "./GameLensBadge";
import { InsightsRoundButton } from "./InsightsRoundButton";
import { LensLinkWrapper } from "./LensLinkWrapper";
import { ActionSection, BadgesContainer } from "./LensRow.styles";
import { LensRowSubmissionErrorModalTrigger } from "./LensRowSubmissionErrorModalTrigger";
import { LensSurfaceTypeTag } from "./LensSurfaceTypeTag";
import { LensCreatorPayoutBadge } from "./lens-creator-payout-badge";

export interface LensRowProps {
    style: CSSProperties;
    lens: MyLensesLens;
    rowIndex: number;
    submissionErrorsProps: LensModalProps;
    distributionRestrictionsProps: LensModalProps;
}

export const LensRow: FC<LensRowProps> = ({
    lens,
    rowIndex,
    style,
    submissionErrorsProps,
    distributionRestrictionsProps,
}) => {
    const { acceptedLatestVersion } = useTos(TosKey.LensCreatorPayoutTos);

    const { shouldShowAdsAvailability } = useShouldShowAdsAvailability();

    const { isPermitted: isPermittedToViewInsights } = usePermittedBasedOnLensFolderRoleAction(
        lens.lensAccountId?.lensSpaceId ?? "",
        ROLE_ACTIONS.READ_LENS_INSIGHTS,
    );
    const scanCode = useMemo(() => getLensScanCode(lens.qrCode), [lens.qrCode]);
    const copyDeeplinkToClipboard = useCallback(() => {
        if (lens.qrCode?.deeplink) {
            copyToClipboard(lens.qrCode.deeplink);
        }
    }, [lens.qrCode?.deeplink]);

    const { hasRejectionErrors, hasSubmissionErrors } = useSubmissionErrors(lens.id);

    const { actionsState } = useLensActionsState(lens);
    const actionsConfig = actionsState.actionsConfig;
    const { isHovered: isStatusHovered, handleMouseEnter, handleMouseLeave } = useHoveredState();

    const isLensLocked = LOCKED_BASE_STATUSES.has(lens.status);
    const availableForAds = isAvailableForAds(lens.disabledDestinationTypesList, lens.status);

    const actionsCount = 1;

    const destinations = useLensDestinationTypesFromLens(lens);

    const onDestinationHeaderClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            distributionRestrictionsProps.setLens(lens);
            distributionRestrictionsProps.showModal();
            event.stopPropagation();
        },
        [distributionRestrictionsProps, lens],
    );

    const lensData = (
        <>
            <IconSection data-testid="lens-row-icon" className={iconWidth}>
                <LensIcon src={lens.iconUrl} alt={`${lens.name} Lens Icon`} />
            </IconSection>
            <Flexbox className={cx(tableTextStyle, nameWidth)} alignItems="center" justifyContent="space-between">
                <TruncateComponent data-testid="lens-row-name" content={<>{lens.name}</>} />
                <BadgesContainer>
                    <LensSurfaceTypeTag surfaceType={lens.surfaceType} />
                    <LensCreatorPayoutBadge
                        lensCreatorPayoutEligibility={lens.lensCreatorPayoutEligibility}
                        acceptedLatestTosVersion={acceptedLatestVersion}
                        exclusiveLensStatus={lens.exclusiveLensStatus}
                    />
                    <GameLensBadge
                        primaryCategoryId={lens.primaryCategoryId}
                        isGameUserProvided={lens.isGameUserProvided}
                    />
                </BadgesContainer>
            </Flexbox>

            <LensUpdateAtDate data-testid="lens-row-update-date" seconds={lens.updatedAt?.seconds ?? 0} />

            {shouldShowAdsAvailability && (
                <AdsAvailabilitySection className={adsAvailabilityWidth}>
                    {availableForAds ? <CheckIcon fill={Colors.Green.V200} /> : <NotAvailableBar />}
                </AdsAvailabilitySection>
            )}
            <StatusSection
                data-testid="lens-row-lens-status"
                className={statusWidth}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <LensStatusWithWarning
                    status={lens.status}
                    hasSubmissionErrors={hasSubmissionErrors}
                    destinations={destinations}
                    showDestinationsText={true}
                    loading={false}
                    hasDistributionRestrictions={
                        distributionRestrictionsProps.isEnabled && lens.distributionRestrictionsList.length > 0
                    }
                />

                {destinations.length > 0 && !isLensLocked && isStatusHovered && (
                    <Flexbox justifyContent="center">
                        <LensDestinationsTooltip
                            lens={lens}
                            numDestinations={destinations.length}
                            onDestinationHeaderClick={onDestinationHeaderClick}
                            isDisabledInDiscoverEnabled={distributionRestrictionsProps.isEnabled}
                        />
                    </Flexbox>
                )}
            </StatusSection>

            <VisibilityLensRow
                dataTestId="lens-row-visibility"
                className={visibilityWidth}
                label={VISIBILITY_OPTION_LABEL_MAP[lens.visibility]}
                Icon={VISIBILITY_OPTION_ICON_MAP[lens.visibility]}
            />
        </>
    );

    return (
        <LensContainer
            id={lens.id}
            data-row-index={rowIndex}
            data-testid="lens-row"
            style={style}
            hasErrors={hasRejectionErrors}
        >
            <LensDataContainer>
                <LensDataContainer>
                    <LensLinkWrapper lens={lens}>{lensData}</LensLinkWrapper>
                </LensDataContainer>

                {hasRejectionErrors ? (
                    <LensRowSubmissionErrorModalTrigger
                        lens={lens}
                        submissionErrorsProps={submissionErrorsProps}
                        className={actionsWidth(actionsCount)}
                    />
                ) : (
                    <ActionSection className={actionsWidth(actionsCount)}>
                        <CopyDeeplink
                            active={actionsConfig.copyDeeplink}
                            copyDeeplinkToClipboard={copyDeeplinkToClipboard}
                        />
                        <InsightsRoundButton
                            isDisabled={!isPermittedToViewInsights}
                            lensId={lens.id}
                            orgId={lens.organizationId}
                            lensFolderId={lens.lensAccountId?.lensSpaceId ?? undefined}
                        />
                        <MeatBallsMenu
                            scanCode={scanCode}
                            lensId={lens.id}
                            lensCreatorPayoutEligibility={lens.lensCreatorPayoutEligibility}
                            actionsConfig={actionsConfig}
                        >
                            <Button shape="circle" title="View More Actions">
                                <EllipsisIcon />
                            </Button>
                        </MeatBallsMenu>
                    </ActionSection>
                )}
            </LensDataContainer>
        </LensContainer>
    );
};
